import { useEffect, useState } from "react";

// Safari 26 Liquid Glass: collapse Safari's bars in iPhone landscape via a document scroll runway.

const RUNWAY_PX = 320;
const PARK_Y = 96;
const PROBE_ID = "ios-lvh-probe";
const TOP_TAP_GUARD_PX = 30;

// iPhone Safari in a normal tab (not a homescreen PWA); iPad excluded on purpose.
export const isIOSSafariTab = (): boolean => {
  if (typeof navigator === "undefined" || typeof window === "undefined")
    return false;

  const ua = navigator.userAgent;
  const isIPhone = /iPhone|iPod/.test(ua);

  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as any).standalone === true;

  return isIPhone && !isStandalone;
};

type Options = {
  enabled: boolean;
};

type Result = {
  isIOS: boolean;
  active: boolean;
  chromeCollapsed: boolean;
};

export function useIosLandscapeChrome({ enabled }: Options): Result {
  const [isIOS] = useState(isIOSSafariTab);
  const active = isIOS && enabled;
  const [chromeCollapsed, setChromeCollapsed] = useState(false);

  useEffect(() => {
    if (!active) return;

    const html = document.documentElement;
    const body = document.body;
    const app = document.querySelector<HTMLElement>(".App");
    if (!app) return;

    const prevHtml = {
      overflowY: html.style.overflowY,
      height: html.style.height,
    };
    const prevBody = {
      overflowY: body.style.overflowY,
      height: body.style.height,
      minHeight: body.style.minHeight,
      padding: body.style.padding,
    };
    const prevApp = {
      position: app.style.position,
      top: app.style.top,
      right: app.style.right,
      bottom: app.style.bottom,
      left: app.style.left,
      height: app.style.height,
      padding: app.style.padding,
    };

    // Document becomes the scroller (Y only); the app shell is pinned on top of the runway.
    html.style.overflowY = "auto";
    html.style.height = "auto";
    body.style.overflowY = "visible";
    body.style.height = "auto";
    body.style.minHeight = `calc(100dvh + ${RUNWAY_PX}px)`;
    body.style.padding = "0";

    app.style.position = "fixed";
    app.style.top = "0";
    app.style.right = "0";
    app.style.bottom = "0";
    app.style.left = "0";
    app.style.height = "100dvh";
    app.style.padding =
      "env(safe-area-inset-top) 0 env(safe-area-inset-bottom) 0";

    // 100lvh probe = viewport height with bars collapsed; compare to detect the state.
    let probe = document.getElementById(PROBE_ID);
    if (!probe) {
      probe = document.createElement("div");
      probe.id = PROBE_ID;
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:fixed;top:0;left:0;width:1px;height:100lvh;visibility:hidden;pointer-events:none";
      body.appendChild(probe);
    }

    const measure = () => {
      const lvh = probe!.offsetHeight;
      setChromeCollapsed(lvh > 0 && lvh - window.innerHeight < 4);
    };

    const park = () => {
      if (window.scrollY < PARK_Y) {
        window.scrollTo({
          top: PARK_Y,
          left: 0,
          behavior: "instant" as ScrollBehavior,
        });
      }
    };

    // Re-park after the gesture settles, never during an active touch.
    let parkTimer: number | undefined;
    let touching = false;
    const schedulePark = () => {
      window.clearTimeout(parkTimer);
      parkTimer = window.setTimeout(() => {
        if (!touching) park();
      }, 250);
    };
    const onTouchStart = () => {
      touching = true;
      window.clearTimeout(parkTimer);
    };
    const onTouchEnd = () => {
      touching = false;
      schedulePark();
    };

    requestAnimationFrame(() => {
      park();
      measure();
    });

    window.addEventListener("scroll", schedulePark, { passive: true });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);

    return () => {
      window.clearTimeout(parkTimer);
      window.removeEventListener("scroll", schedulePark);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
      probe?.remove();

      html.style.overflowY = prevHtml.overflowY;
      html.style.height = prevHtml.height;
      body.style.overflowY = prevBody.overflowY;
      body.style.height = prevBody.height;
      body.style.minHeight = prevBody.minHeight;
      body.style.padding = prevBody.padding;
      app.style.position = prevApp.position;
      app.style.top = prevApp.top;
      app.style.right = prevApp.right;
      app.style.bottom = prevApp.bottom;
      app.style.left = prevApp.left;
      app.style.height = prevApp.height;
      app.style.padding = prevApp.padding;

      window.scrollTo(0, 0);
      setChromeCollapsed(false);
    };
  }, [active]);

  // Collapsed bars re-expand on a tap near the top edge — keep the app UI clear of it.
  useEffect(() => {
    if (!active) return;
    const app = document.querySelector<HTMLElement>(".App");
    if (!app) return;

    app.style.paddingTop = chromeCollapsed
      ? `max(env(safe-area-inset-top), ${TOP_TAP_GUARD_PX}px)`
      : "env(safe-area-inset-top)";
  }, [active, chromeCollapsed]);

  return { isIOS, active, chromeCollapsed };
}
